<script lang="ts" setup>
export interface ConfirmDialogProps {
  cancelId?: string;
  cancelText?: string;
  confirmColor?: string;
  confirmId?: string;
  confirmText?: string;
  /** Whether dialog should close immediately upon confirmation */
  closeOnConfirm?: boolean;
  /** Whether confirmation is disabled/pending */
  disabled?: boolean;
  modelValue: boolean;
  loading?: boolean;
  title: string;
  /**
   * Confirmation dialog type
   *
   * - "confirm" - Confirm/cancel buttons
   * - "alert" - Confirm button
   */
  type?: "confirm" | "alert";
}

const props = withDefaults(defineProps<ConfirmDialogProps>(), {
  cancelId: undefined,
  cancelText: undefined,
  confirmColor: "primary",
  confirmId: undefined,
  confirmText: undefined,
  closeOnConfirm: true,
  disabled: false,
  loading: false,
  type: "confirm",
});

const emit = defineEmits<{
  (event: "update:modelValue", open: boolean): void;
  (event: "confirm"): void;
  (event: "cancel"): void;
}>();

const handleCancel = () => {
  emit("cancel");
  emit("update:modelValue", false);
};

const handleConfirm = () => {
  emit("confirm");

  // Confirmation dialogs might not always close immediately after confirmation (ie. operation pending, etc)
  if (props.closeOnConfirm) {
    emit("update:modelValue", false);
  }
};
</script>

<template>
  <VDialog
    max-width="400"
    min-width="300"
    :model-value="modelValue"
    v-bind="$attrs"
    @update:model-value="(open: boolean) => emit('update:modelValue', open)"
  >
    <VCard>
      <VCardTitle>{{ title }}</VCardTitle>
      <VCardText v-if="$slots.default">
        <slot />
      </VCardText>
      <VCardActions>
        <ActionBar right>
          <VBtn v-if="type === 'confirm'" :id="cancelId" :disabled="loading" @click="handleCancel">
            {{ cancelText ?? "Cancel" }}
          </VBtn>
          <VBtn
            :id="confirmId"
            :color="confirmColor"
            :disabled="disabled"
            :loading="loading"
            variant="tonal"
            @click="handleConfirm"
          >
            {{ confirmText ?? "Confirm" }}
          </VBtn>
        </ActionBar>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
// NOTE: Fix for dialog overlay not expanding (needed to expand smaller dialog content)
/*.v-dialog :deep(.v-overlay__content) {
  width: 100%;
}*/

.v-dialog .v-card-text {
  padding: spacing(2) spacing(4) !important;
  white-space: break-spaces;
}
</style>
